import type {
  Board, PlacedEmitter, InventoryEmitter, Pos, ClueStatus,
} from "../types.ts";
import { posKey } from "../types.ts";
import { boardRows, boardCols, getCell } from "./grid.ts";
import {
  computeSignalMap, evaluateClues, distinctRotations,
} from "./signal.ts";

interface SolverState {
  board: Board;
  fixed: PlacedEmitter[];
  inventory: InventoryEmitter[];
  maxSolutions: number;
  solutions: PlacedEmitter[][];
  deadline: number;
  emptyCells: Pos[];
}

/**
 * Find solutions for a puzzle.
 * Places each inventory emitter on an empty cell with some rotation,
 * pruning when any clue is exceeded.
 * Stops after finding `maxSolutions` solutions.
 */
export function findSolutions(
  board: Board,
  fixed: PlacedEmitter[],
  inventory: InventoryEmitter[],
  maxSolutions: number = 2,
  timeLimitMs: number = 5000
): PlacedEmitter[][] {
  const rows = boardRows(board);
  const cols = boardCols(board);

  // Collect empty cells (where emitters can be placed)
  const emptyCells: Pos[] = [];
  const fixedSet = new Set(fixed.map((e) => posKey(e.pos)));

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = getCell(board, { row: r, col: c });
      if (cell && cell.kind === "empty" && !fixedSet.has(posKey({ row: r, col: c }))) {
        emptyCells.push({ row: r, col: c });
      }
    }
  }

  const state: SolverState = {
    board,
    fixed,
    inventory,
    maxSolutions,
    solutions: [],
    deadline: Date.now() + timeLimitMs,
    emptyCells,
  };

  backtrack(state, [...fixed], 0);
  return state.solutions;
}

function backtrack(
  state: SolverState,
  placed: PlacedEmitter[],
  invIdx: number
): void {
  if (state.solutions.length >= state.maxSolutions) return;
  if (Date.now() > state.deadline) return;

  // All inventory emitters placed — check if solution
  if (invIdx >= state.inventory.length) {
    const signalMap = computeSignalMap(state.board, placed);
    const clues = evaluateClues(state.board, signalMap);
    const allSatisfied = [...clues.values()].every((s) => s === "satisfied");
    if (allSatisfied) {
      // Store only the non-fixed emitters as the "solution"
      state.solutions.push([...placed]);
    }
    return;
  }

  const emitter = state.inventory[invIdx]!;
  const rotations = distinctRotations(emitter.type.shape);

  for (const pos of state.emptyCells) {
    // Check pos not already occupied by another placed emitter
    const posStr = posKey(pos);
    if (placed.some((p) => posKey(p.pos) === posStr)) continue;

    for (let rot = 0; rot < rotations; rot++) {
      const candidate: PlacedEmitter = {
        id: emitter.id,
        type: emitter.type,
        pos,
        rotation: rot,
        fixed: false,
      };

      placed.push(candidate);

      // Prune: check if any clue is already exceeded
      if (!hasExceededClue(state.board, placed)) {
        backtrack(state, placed, invIdx + 1);
      }

      placed.pop();

      if (state.solutions.length >= state.maxSolutions) return;
      if (Date.now() > state.deadline) return;
    }
  }
}

/** Quick check: does the current partial placement exceed any clue? */
function hasExceededClue(board: Board, emitters: PlacedEmitter[]): boolean {
  const signalMap = computeSignalMap(board, emitters);
  const clues = evaluateClues(board, signalMap);
  for (const status of clues.values()) {
    if (status === "exceeded") return true;
  }
  return false;
}

/** Check if a puzzle has exactly one solution */
export function hasUniqueSolution(
  board: Board,
  fixed: PlacedEmitter[],
  inventory: InventoryEmitter[],
  timeLimitMs: number = 5000
): boolean {
  const solutions = findSolutions(board, fixed, inventory, 2, timeLimitMs);
  return solutions.length === 1;
}
